import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, MapPin, Clock, Send, HelpCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
interface ContactPageProps {
  onNavigate: (page: string) => void;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  const {
    success
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'support',
    message: ''
  });
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      success('Сообщение успешно отправлено! Мы ответим вам в ближайшее время.');
      setFormData({
        name: '',
        email: '',
        subject: 'support',
        message: ''
      });
    }, 1500);
  };
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-18807">
      <div className="bg-white border-b border-slate-200 pt-6 pb-12" data-id="element-18808">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18809">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Контакты'
        }]} className="mb-8" data-id="element-18810" />
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-id="element-18811">
            Свяжитесь с нами
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl" data-id="element-18812">
            У вас есть вопросы, предложения или нужна помощь? Наша команда
            всегда готова помочь.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-18813">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-18814">
          {/* Contact Info */}
          <div className="lg:col-span-5 space-y-8" data-id="element-18815">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm" data-id="element-18816">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-18817">
                Контактная информация
              </h2>

              <div className="space-y-6" data-id="element-18818">
                <div className="flex items-start gap-4" data-id="element-18819">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-18820">
                    <Mail className="w-5 h-5 text-indigo-600" data-id="element-18821" />
                  </div>
                  <div data-id="element-18822">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-18823">
                      Email поддержки
                    </div>
                    <a href="mailto:support@astriqo.com" className="text-slate-900 font-medium hover:text-indigo-600 transition-colors" data-id="element-18824">
                      support@astriqo.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-18825">
                  <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0" data-id="element-18826">
                    <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-18827" />
                  </div>
                  <div data-id="element-18828">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-18829">
                      Telegram
                    </div>
                    <a href="#" className="text-slate-900 font-medium hover:text-sky-600 transition-colors" data-id="element-18830">
                      @astriqo_support
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-18831">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-18832">
                    <Clock className="w-5 h-5 text-emerald-600" data-id="element-18833" />
                  </div>
                  <div data-id="element-18834">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-18835">
                      Режим работы
                    </div>
                    <div className="text-slate-900 font-medium" data-id="element-18836">
                      Ежедневно, 10:00 - 20:00 (МСК)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-2xl p-8 border border-indigo-100" data-id="element-18837">
              <div className="flex items-center gap-3 mb-4" data-id="element-18838">
                <HelpCircle className="w-6 h-6 text-indigo-600" data-id="element-18839" />
                <h2 className="text-lg font-bold text-indigo-900" data-id="element-18840">
                  Частые вопросы
                </h2>
              </div>
              <p className="text-indigo-800/80 mb-6 text-sm leading-relaxed" data-id="element-18841">
                Возможно, мы уже ответили на ваш вопрос. Посетите раздел FAQ,
                чтобы найти информацию о возвратах, отчетах и консультациях.
              </p>
              <Button variant="outline" className="w-full bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-100" onClick={() => onNavigate('faq')} data-id="element-18842">
                Перейти в FAQ
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7" data-id="element-18843">
            <div className="bg-white rounded-2xl p-8 sm:p-10 border border-slate-200 shadow-xl" data-id="element-18844">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-18845">
                Написать нам
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-18846">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-18847">
                  <div className="space-y-2" data-id="element-18848">
                    <label className="text-sm font-medium text-slate-700" data-id="element-18849">
                      Ваше имя
                    </label>
                    <Input placeholder="Иван Иванов" required value={formData.name} onChange={(e: any) => handleChange('name', e.target ? e.target.value : e)} data-id="element-18850" />
                  </div>
                  <div className="space-y-2" data-id="element-18851">
                    <label className="text-sm font-medium text-slate-700" data-id="element-18852">
                      Email
                    </label>
                    <Input type="email" placeholder="ivan@example.com" required value={formData.email} onChange={(e: any) => handleChange('email', e.target ? e.target.value : e)} data-id="element-18853" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-18854">
                  <label className="text-sm font-medium text-slate-700" data-id="element-18855">
                    Тема обращения
                  </label>
                  <select className="w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" value={formData.subject} onChange={e => handleChange('subject', e.target.value)} data-id="element-18856">
                    <option value="support" data-id="element-18857">Техническая поддержка</option>
                    <option value="report" data-id="element-18858">Вопрос по отчету</option>
                    <option value="consultation" data-id="element-18859">Вопрос по консультации</option>
                    <option value="partnership" data-id="element-18860">Сотрудничество</option>
                    <option value="other" data-id="element-18861">Другое</option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-18862">
                  <label className="text-sm font-medium text-slate-700" data-id="element-18863">
                    Сообщение
                  </label>
                  <textarea className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all min-h-[150px] resize-y" placeholder="Опишите вашу проблему или вопрос максимально подробно..." required value={formData.message} onChange={e => handleChange('message', e.target.value)} data-id="element-18864"></textarea>
                </div>

                <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={isSubmitting} data-id="element-18865">
                  {isSubmitting ? 'Отправка...' : <>
                      <Send className="w-4 h-4 mr-2" data-id="element-18866" />
                      Отправить сообщение
                    </>}
                </Button>

                <p className="text-xs text-slate-500 mt-4" data-id="element-18867">
                  Нажимая кнопку «Отправить сообщение», вы соглашаетесь с{' '}
                  <button type="button" onClick={() => onNavigate('privacy')} className="text-indigo-600 hover:underline" data-id="element-18868">
                    Политикой конфиденциальности
                  </button>
                  .
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
}